import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaseLinksComponent } from 'app/case-links/case-links.component';
import { CaseCommentsComponent } from 'app/shared/components/case-comments/case-comments.component';
import { CaseHistoryComponent } from 'app/shared/components/case-history/case-history.component';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ActivityFormComponent } from 'app/shared/components/forms/activity-form/activity-form.component';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { CaseRecordAppsFormComponent } from 'app/shared/components/forms/case-record-apps-form/case-record-apps-form.component';
import { CaseRecordProductCompanyFormComponent } from 'app/shared/components/forms/case-record-product-company-form/case-record-product-company-form.component';
import { CaseRecordStcFormComponent } from 'app/shared/components/forms/case-record-stc-form/case-record-stc-form.component';
import { CaseTeamFormComponent } from 'app/shared/components/forms/case-team-form/case-team-form.component';
import { CustomerDetailsFormComponent } from 'app/shared/components/forms/customer-details-form/customer-details-form.component';
import { InterestedPartiesFormComponent } from 'app/shared/components/forms/interested-parties-form/interested-parties-form.component';
import { ProblemDetailsFormComponent } from 'app/shared/components/forms/problem-details-form/problem-details-form.component';
import { ProcessDetailsFormComponent } from 'app/shared/components/forms/process-details-form/process-details-form.component';
import { ProductDetailsEpartsFormComponent } from 'app/shared/components/forms/product-details-eparts-form/product-details-eparts-form.component';
import { ProductDetailsFormComponent } from 'app/shared/components/forms/product-details-form/product-details-form.component';
import { TicketInfoFormComponent } from 'app/shared/components/forms/ticket-info-form/ticket-info-form.component';
import { RichTextEditorComponent } from 'app/shared/components/forms/fields/rich-text-editor/rich-text-editor.component';
import { TabsWithContentComponent } from 'app/shared/components/tabs-with-content/tabs-with-content.component';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { finalize, first, Subject, take, takeUntil } from 'rxjs';
import { CaseFormService } from './case-form.service';
import { CaseFormModel } from './case-form.model';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { CaseStatusEnum, NextActionStatusEmum } from 'app/shared/enums/case-status.enum';
import { TicketClosureModalComponent } from 'app/shared/components/ticket-closure-modal/ticket-closure-modal.component';
import { ChatDialogComponent } from 'app/shared/components/chat-dialog/chat-dialog.component';
import { HelperService } from 'app/shared/services/helper.service';
import { AuthService } from 'app/shared/services/auth.service';
import { UsersService } from 'app/shared/services/users.service';
import { IUser, IUserLock } from 'app/shared/models/users.model';
import { ImpersonateService } from 'app/profile/impersonate-user/impersonate.service';
import { ICredentials } from 'app/shared/models/credentials.model';
import { DiscardChangesService } from 'app/shared/services/discard-changes.service';
import { CanComponentDeactivate } from 'app/shared/guards/can-deactivate.guard';
import { USER_ROLES } from 'app/shared/enums/user-roles.enum';
import { SidebarService } from 'app/shell/sidebar/sidebar.service';
import { AlertNotificationComponent } from 'app/shared/components/alert-notification/alert-notification.component';
import { EscalationFormComponent } from 'app/shared/components/forms/escalation-form/escalation-form.component';
import { IEscalateCase } from 'app/shared/models/escalate-case.model';
import { HistoryEntityNamesEnum, HistoryTabsEnum } from 'app/shared/enums/history-entity.enum';
import { IHistoryTab } from 'app/shared/models/history.model';
import { CaseResolutionContainmentFormComponent } from 'app/shared/components/forms/case-resolution-containment-form/case-resolution-containment-form.component';
import { CaseClosureSurveyComponent } from 'app/shared/components/case-closure-survey/case-closure-survey.component';
import { ISurveyTeam } from 'app/shared/models/case-closure-survey.model';
import { DropdownTypeEnum } from 'app/shared/enums/dropdowns.enum';
import { BreadcrumbService } from 'app/shared/services/breadcrumb.service';
import { CaseLockService } from 'app/shared/services/case-lock.service';
import { CaseTypeEnum } from 'app/shared/enums/case-type.enum';

@Component({
  selector: 'app-case-form',
  standalone: true,
  imports: [
    CaseFormSectionContainerComponent,
    TranslateModule,
    ReactiveFormsModule,
    CustomerDetailsFormComponent,
    ProblemDetailsFormComponent,
    CaseResolutionContainmentFormComponent,
    ProductDetailsFormComponent,
    ProductDetailsEpartsFormComponent,
    ProcessDetailsFormComponent,
    InterestedPartiesFormComponent,
    RichTextEditorComponent,
    TabsWithContentComponent,
    CaseRecordProductCompanyFormComponent,
    CaseRecordAppsFormComponent,
    CaseRecordStcFormComponent,
    CaseCommentsComponent,
    CaseHistoryComponent,
    TicketInfoFormComponent,
    ActivityFormComponent,
    EscalationFormComponent,
    CaseTeamFormComponent,
    CaseLinksComponent,
    ConfirmationDialogComponent,
    TitleHeaderComponent,
    TicketClosureModalComponent,
    AlertNotificationComponent,
    ChatDialogComponent,
    CaseClosureSurveyComponent,
    FormsModule,
    RouterModule,
    CommonModule
  ],
  templateUrl: './case-form.component.html',
  styleUrl: './case-form.component.scss'
})
export class CaseFormComponent implements OnInit, CanComponentDeactivate {
  private readonly _destroy$ = new Subject<void>();
  private readonly _destroySecond$ = new Subject<void>();
  caseIdentifier!: string;
  caseTitle!: string;
  tabs: string[] = ['overview', 'collaboration', 'comments', 'history'];
  historyTabs: IHistoryTab[] = [];
  selectedTab: string = this.tabs[0];
  caseForm!: FormGroup | any;
  linksForm!: FormGroup;
  extended: boolean = true;
  viewMode: boolean = false;
  linksEditMode: boolean = false;
  severityItems: any[] = [];
  caseStageItems: any[] = [];
  actionStatusItems: any[] = [];
  statusItems: any[] = [];
  caseId!: number;
  caseStage!: string;
  caseStatus!: string;
  currentCaseStage!: string;
  loading: boolean = false;
  showVerifiedKnowledge: boolean = false;
  chatOpened!: boolean;
  chatCollapsed!: boolean;
  chatParticipants: number[] = [];
  isChatAvailable!: boolean;
  commentsFilterForm!: FormGroup | any;
  linksEditable: boolean = true;
  escalationData!: IEscalateCase | null;
  isSurvey!: boolean;
  displaySurveyBanner!: boolean;
  backButtonLink!: string;

  credentials!: ICredentials | undefined;
  uploadedFiles: any[] = [];
  attachmentsTouched!: boolean;
  surveyTeam!: ISurveyTeam;
  nextActionValidators!: boolean;
  customerRegion!: string;
  productSuite!: string;
  lockUser!: IUserLock;
  caseObject!: CaseFormModel;
  isInitializingForm: boolean = true;
  isCaseClosed!: boolean;
  caseType: string = CaseTypeEnum.CASE;
  isAdmin!: boolean;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private router: Router,
    private configurationService: ConfigurationService,
    private caseFormService: CaseFormService,
    private activeRoute: ActivatedRoute,
    private translate: TranslateService,
    private toastr: ToastrService,
    private helperService: HelperService,
    private authService: AuthService,
    private usersService: UsersService,
    private impersonateService: ImpersonateService,
    private caseLockService: CaseLockService,
    private discardChangesService: DiscardChangesService,
    private sidebarService: SidebarService,
    private breadcrumbService: BreadcrumbService,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.credentials = this.authService.getCredentials();
    this.isAdmin = this.credentials?.role === USER_ROLES.ADMIN_USER;
    this.createForm();
    this.getSimpleDropdowns();
    this.getUsers();

    this.activeRoute.params
      .pipe(takeUntil(this._destroy$))
      .subscribe((params: any) => {
        if (params['id']) {
          this.caseId = Number(params['id']);
          this.backButtonLink = this.breadcrumbService.getPreviousUrl();
          this.getCase(this.caseId, true);
          this.viewMode = true;
        }

        this.activeRoute.queryParams
          .pipe(takeUntil(this._destroySecond$))
          .subscribe((queryParams) => {
            if (this.caseId) {
              if (queryParams['tab'] && this.tabs.some(t => t === queryParams['tab'])) {
                this.onSelectedTab(queryParams['tab'], true);
              } else {
                this.onSelectedTab(this.tabs[0], true);
              }
            }
          });
      });

    this.caseFormService.caseEscalated$.pipe(takeUntil(this._destroy$)).subscribe((res: IEscalateCase) => {
      if (res) {
        res = { ...res, escalatedDate: new Date() };
        this.escalationData = res;
        const isDirtyForm = this.caseForm.dirty;
        this.caseForm.patchValue({ caseEscalation: { ...res } }, { emitEvent: false });
        if (!isDirtyForm) {
          setTimeout(() => {
            this.caseForm.markAsPristine();
          });
        }
      }
    });
  }

  createForm() {
    this.caseForm = this.fb.group({});
    this.caseForm.addControl('id', new FormControl(null, []));
    this.caseForm.addControl('caseRecord', new FormGroup({}, []));
    this.linksForm = this.fb.group({});
    this.commentsFilterForm = new FormGroup({
      showChatsOnly: new FormControl(false)
    });

    setTimeout(() => {
      if (this.caseFormService.selectedSerialForm) {
        this.populateFormFromSerialNumber();
      }
    })
  }

  getSimpleDropdowns() {
    this.severityItems = this.configurationService.getSeverityItems();
    this.caseStageItems = this.configurationService.getCaseStageItems();
    this.actionStatusItems = this.configurationService.getNextActionStatusItems();
    this.statusItems = this.configurationService.getStatusItems();
  }

  getUsers() {
    this.usersService.getUsers('')
      .pipe(take(1))
      .subscribe((res: IUser[]) => {
        this.usersService.setUsers(res);
      })
  }

  onSelectedTab(tabName: string, isOnInit?: boolean): void {
    this.selectedTab = tabName;
    this.setSelectedTabToUrlQueryParams(tabName);
  }

  setSelectedTabToUrlQueryParams(selectedCard: string) {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { tab: selectedCard },
      queryParamsHandling: 'merge'
    });
  }

  getCase(id: number, fromAPISource?: boolean) {
    this.isInitializingForm = true;
    if (!fromAPISource) {
      this.prepareAndSetFormData(this.caseObject, false);
    } else {
      this.loading = true;

      this.caseFormService.getCase(id)
        .pipe(
          take(1)
        )
        .subscribe((res: any) => {
          this.prepareAndSetFormData(res, true);
          this.loading = false;
        })
    }
  }

  prepareAndSetFormData(res: any, fromAPI: boolean) {
    this.caseObject = res;
    this.lockUser = res.editableUser as IUserLock;
    this.attachmentsTouched = false;
    this.caseIdentifier = res.caseIdentifier;
    this.caseTitle = res.caseTitle;
    this.isCaseClosed = res.caseStage === CaseStatusEnum.CLOSED_RESOLVED || res.caseStage === CaseStatusEnum.CLOSED_NOT_RESOLVED;
    this.historyTabs = [
      { name: HistoryTabsEnum.OVERVIEW, value: HistoryEntityNamesEnum.CASE, entityId: this.caseId, hidden: true }
    ]
    if (fromAPI) {
      this.helperService.convertUsersToObjects(res, ['bookmarkOnly', 'allActivityUpdates', 'caseClosureNotifications'], true);
    }
    this.uploadedFiles = (res.attachments ?? []).map((file: any) => ({
      id: file.id,
      name: file.fileName,
      blobStorageId: file.storageFileId,
      type: file.mimeType,
    }));
    this.surveyTeam = {
      ticketOwnerId: res.ticketOwnerId,
      ticketOwner: res.ticketOwner,
      internalCaseContactId: res.internalCaseContactId,
      internalCaseContact: res.internalCaseContact,
      tsmNotifiedId: undefined,
      tsmNotified: '',
    }
    this.caseStage = res.caseStage;
    this.caseStatus = res.caseStatus;
    this.customerRegion = res.customerRegion;
    this.productSuite = res.productSuite;

    this.onChangeCaseStageEmiterSubscription();

    if (this.credentials?.role !== USER_ROLES.ADMIN_USER && this.credentials?.role !== USER_ROLES.REGIONAL_TECH_SUPPORT && this.credentials?.id !== res.ticketOwnerId) {
      this.caseStageItems = this.configurationService.getCaseStageItems().filter(i => !i.visibleForAdminsOnly);
    } else {
      this.caseStageItems = this.configurationService.getCaseStageItems()
    }
    if (res.nextActionStatus !== NextActionStatusEmum.InitialResponseRequired) {
      this.actionStatusItems = this.configurationService.getNextActionStatusItems().filter(s => s.value !== NextActionStatusEmum.InitialResponseRequired);
    }
    const ticketParticipantsIds: number[] = [res.createdById, res.ticketOwnerId, res.internalCaseContactId, res.stcResourceId, res.pcResourceId, res.appsResourceId, res.nextActionOwnerId];
    this.isChatAvailable = ticketParticipantsIds.some((id: number) => id === this.credentials?.id) && !this.impersonateService.impersonatedUser;
    this.currentCaseStage = res.caseStage;

    if (res && res.serialNumber && !res.epartSerialNumber) {
      this.caseForm.get('epartSerialNumber').setValue(res.serialNumber);
    }
    if (res && res.problemFirstOccurredDate && !res.nonConformanceDate) {
      this.caseForm.get('nonConformanceDate').setValue(res.problemFirstOccurredDate);
    }

    if (res.customerContactEmail) {
      this.caseForm.controls['customerContactEmail'].setValidators(res.customerContactEmail.toLowerCase() === 'n/a' ? null : [Validators.email]);
      this.caseForm.get('customerContactEmail')?.updateValueAndValidity();
    }

    this.setNextActionValidators(res.ticketOwnerId);

    this.updateLinksForm(res.links);

    this.setProductSuiteProductCompany(res);

    this.updateAttachments(this.uploadedFiles ?? []);

    if (res.caseEscalation) {
      if (fromAPI) {
        this.helperService.convertUsersToObjects(res.caseEscalation, ['customerEscalatedByWhomInternal', 'escalationTeam'], false);
      }
      this.escalationData = res.caseEscalation;
      this.sidebarService.ticketEscalationData = res.caseEscalation;
    }

    this.caseForm.patchValue(res, { emitEvent: false });

    setTimeout(() => {
      if ((this.isCaseClosed && !this.isAdmin) || !this.lockUser.isEditable) {
        this.displaySurveyBanner = res.surveyAvailable && res.createdById === this.credentials?.id;
        this.caseForm.disable();
        this.linksEditable = false;
      } else {
        const caseStage: FormControl = this.caseForm.get('caseStage');
        if (this.isCaseClosed && this.isAdmin) {
          caseStage.disable();
        } else {
          caseStage.enable();
        }
        this.linksEditable = true;
        this.sidebarService.ticketEscalationId = this.caseId;
      }
      if (this.isCaseClosed) {
        this.showVerifiedKnowledge = true;
        this.enableVerifiedKnowledge();
      }
      this.caseForm.updateValueAndValidity();
      this.caseForm.markAsPristine();
      this.linksForm.markAsPristine();
      this.isInitializingForm = false;
    })

    this.caseFormService.onChangeCaseStageEmiter.emit({ value: res.caseStage });
  }

  enableVerifiedKnowledge() {
    if (this.lockUser.isEditable && this.credentials?.role === USER_ROLES.REGIONAL_TECH_SUPPORT) {
      this.caseForm.get('verifiedKnowledge')?.enable();
    }
  }

  populateFormFromSerialNumber() {
    const serialNumberForm: any = this.caseFormService.selectedSerialForm;
    this.caseObject = serialNumberForm;
    this.caseForm.patchValue(serialNumberForm);
    setTimeout(() => {
      this.caseForm.markAsPristine();
    });
  }

  setProductSuiteProductCompany(res: any) {
    this.caseForm.get('productSuite')?.setValue(res.productSuite ? res.productSuite : null);
    this.caseForm.get('productCompany')?.setValue(res.productCompany ? res.productCompany : null);

    this.caseFormService.onChangeProductSuiteAndCompanyEmitter.emit(res.caseStage);
  }

  setNextActionValidators(ticketOwnerId: number) {
    const userIsTicketOwner: boolean = ticketOwnerId === this.credentials?.id;
    const setValidators: boolean = userIsTicketOwner && !this.isCaseClosed;
    this.caseForm.controls['nextActionOwnerId'].setValidators(setValidators ? [Validators.required] : []);
    this.caseForm.controls['nextActionDueDate'].setValidators(setValidators ? [Validators.required] : []);
    this.caseForm.controls['nextActionDescription'].setValidators(setValidators ? [Validators.required] : []);
    this.caseForm.get('nextActionOwnerId')?.updateValueAndValidity();
    this.caseForm.get('nextActionDueDate')?.updateValueAndValidity();
    this.caseForm.get('nextActionDescription')?.updateValueAndValidity();
    this.nextActionValidators = userIsTicketOwner && !this.isCaseClosed;
  }

  updateAttachments(attachemnts: any[]) {
    this.caseForm.get('caseAttachments')?.setValue([...attachemnts]);
    this.caseForm.get('caseAttachments')?.updateValueAndValidity();
  }

  updateLinksForm(linksData: any) {
    if (linksData) {
      let links: any = {};
      const typeCounter: any = {};

      linksData.forEach((element: any) => {
        element.isChanged = false;
        if (!typeCounter[element.type]) {
          typeCounter[element.type] = 0;
        }

        typeCounter[element.type] += 1;
        links[`${element.type}${typeCounter[element.type]}`] = element;
      });

      this.linksForm.get('links')?.patchValue(links, { emitEvents: false });
    }
  }

  submitLinks($event: boolean) {
    this.linksEditMode = $event;
    if (!this.linksEditMode) {
      const links = this.linksForm.get('links')?.value;
      let linksArr: any[] = [];
      Object.keys(links).forEach(key => {
        const linkItem = links[key];

        if (linkItem.isChanged) {
          linkItem.isActive = !!(linkItem.title && linkItem.url);
          if (linkItem.title && linkItem.url || (!linkItem.title && !linkItem.url)) {
            linksArr.push(linkItem);
          }
        }
      });

      const linksFormValue = linksArr;
      if (!linksFormValue.length) {
        return;
      }

      this.caseFormService.saveCaseLinks(this.caseId, linksFormValue)
        .pipe(take(1))
        .subscribe((res: any) => {
          this.toastr.show(this.translate.instant('messages.links_successfully_saved'), ToastTypesEnum.SUCCESS);
          this.patchSavedLinksIntoForm(res);
          this.linksForm.markAsPristine();
        })
    }
  }

  patchSavedLinksIntoForm(savedLinks: any) {
    const linksGroup = this.linksForm.get('links') as FormGroup;
    const savedArray: any[] = Array.isArray(savedLinks) ? savedLinks : (savedLinks?.links ?? []);

    if (!linksGroup || !savedArray.length) {
      return;
    }

    savedArray
      .filter((saved: any) => saved?.title && saved?.url)
      .forEach((saved: any) => {
        const slotKey = Object.keys(linksGroup.controls).find((key: string) => {
          const slotValue = linksGroup.get(key)?.value;
          return slotValue?.type === saved.type
            && slotValue?.title === saved.title
            && slotValue?.url === saved.url;
        });

        if (slotKey) {
          linksGroup.get(slotKey)?.patchValue({
            id: saved.id,
            isActive: saved.isActive,
            isChanged: false
          }, { emitEvent: false });
        }
      });
  }

  submitForm(isEdit?: boolean) {

    const caseRecordForm: FormGroup = this.caseForm.get('caseRecord') as FormGroup;

    if (!this.caseForm.valid) {
      const errorMessage = this.translate.instant('messages.please_fill_in_all_required_fields');

      this.toastr.show(
        errorMessage, ToastTypesEnum.ERROR
      );

      Object.keys(this.caseForm.controls).forEach(field => {
        const control = this.caseForm.get(field);
        if (control && control.status === 'INVALID' && !!control.errors) {
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      });

      Object.keys(caseRecordForm.controls).forEach(field => {
        const control = caseRecordForm.get(field);
        if (control && control.status === 'INVALID' && !!control.errors) {
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      });

      return;
    }

    const caseFormValue: CaseFormModel = this.caseForm.getRawValue();
    const caseRecordFormValue = caseRecordForm.getRawValue();

    if (this.nextActionValidators && this.helperService.isDateInThePast(caseFormValue.nextActionDueDate)) {
      this.toastr.show(this.translate.instant('messages.next_action_due_date_cannot_be_in_the_past'), ToastTypesEnum.ERROR);
      return
    }

    if (Object.keys(caseRecordFormValue).length !== 0) {
      caseFormValue.caseRecord = caseRecordFormValue;
    }

    if (!this.escalationData) {
      delete caseFormValue.caseEscalation;
    }

    this.saveCase(caseFormValue, isEdit);
  }

  saveCase(caseFormValue: CaseFormModel, isEdit?: boolean) {
    this.helperService.convertUsersToStrings(caseFormValue, ['bookmarkOnly', 'allActivityUpdates', 'caseClosureNotifications'], true);

    if (caseFormValue.caseEscalation) {
      this.helperService.convertUsersToStrings(caseFormValue.caseEscalation, ['customerEscalatedByWhomInternal', 'escalationTeam'], false);
    }

    if (!isEdit) {
      caseFormValue.caseRecord = null as any;
      delete caseFormValue.verifiedKnowledge;
    }

    caseFormValue.caseAttachments = (this.caseForm.get('caseAttachments')?.value || []).map((attachment: any) => ({
      id: attachment.id,
      fileName: attachment.name,
      storageFileId: attachment.blobStorageId,
      mimeType: attachment.type
    }));

    this.caseFormService.saveCase(caseFormValue)
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!isEdit) {
          this.caseForm.reset();
          this.caseForm.markAsPristine();
          this.router.navigate(['case-form/' + res]);
        } else {
          this.reloadCase(true);

        }
      });
  }

  reloadCase(fromAPI: boolean) {
    this.getCase(this.caseId, fromAPI);
    this.caseForm.markAsPristine();
    this.linksForm.markAsPristine();
    this.viewMode = true;
  }

  onLeaveCase(tabName?: string) {
    this.discardChangesService.setUnsavedChangesState(this.caseForm.dirty || this.linksForm.dirty);

    if (!this.caseId && !this.caseForm.dirty) {
      this.router.navigate(['']);
      return;
    }

    this.discardChangesService.canDeactivate().then(
      (result) => {
        if (result) {

          if (tabName) {
            this.selectedTab = tabName;
            this.setSelectedTabToUrlQueryParams(tabName);
          }

          if (this.caseId) {
            this.reloadCase(false);
          } else {
            this.caseForm.reset();
            this.linksForm.reset();
            this.caseForm.markAsPristine();
            this.linksForm.markAsPristine();
            this.router.navigate(['']);
          }
        }
      }
    );
  }

  navigateToCommentsTab() {
    this.commentsFilterForm.patchValue({ showChatsOnly: true });
    this.onSelectedTab('comments');
  }

  deEscalateCase(): void {
    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'confirmation-modal',
    });

    modalRef.componentInstance.title = 'label.de_escalate_case';
    modalRef.componentInstance.message = 'messages.are_you_sure_you_want_to_de-escalate_case';
    modalRef.componentInstance.notConfirmActionLabel = 'buttons.no';
    modalRef.componentInstance.confirmActionLabel = 'buttons.yes';

    modalRef.result.then((result) => {
      if (result) {
        this.caseFormService.deEscalateCase({ ...(this.escalationData as IEscalateCase), caseId: this.caseId }).pipe(take(1)).subscribe((res) => {
          if (res) {
            (this.escalationData as IEscalateCase).isEscalated = false;
            (this.escalationData as IEscalateCase).deEscalatedDate = new Date();
            this.sidebarService.ticketEscalationData = this.escalationData;
            this.toastr.show(this.translate.instant('messages.ticket_successfully_de-escalated'), ToastTypesEnum.SUCCESS);
          }

          const isDirtyForm = this.caseForm.dirty;
          this.caseForm.patchValue({ caseEscalation: { ...this.escalationData } }, { emitEvent: false });
          if (!isDirtyForm) {
            setTimeout(() => {
              this.caseForm.markAsPristine();
            });
          }
        });
      }
    });
  }

  openSurvey() {
    const params = {
      type: DropdownTypeEnum.CUSTOMER_CENTER_COMPLEX,
      entry1: this.customerRegion,
      entry2: this.productSuite
    };

    this.configurationService.getSingleDropdownConfigurations(params)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.surveyTeam.tsmNotifiedId = res ? res.user1Id : res;
        this.surveyTeam.tsmNotified = res ? res.user1 : res;
        this.isSurvey = true;
        this.renderer.addClass(document.body, 'no-scroll');
      });
  }

  closeSurvey(removeBanner: boolean) {
    this.isSurvey = false;
    this.renderer.removeClass(document.body, 'no-scroll');
    if (removeBanner) {
      this.displaySurveyBanner = false;
    }
  }

  isUnsavedChanges(): boolean {
    this.discardChangesService.setUnsavedChangesState(this.caseForm.dirty || this.linksForm.dirty);

    return this.discardChangesService.getUnsavedChangesState();
  }

  onChangeCaseStageEmiterSubscription() {
    this.caseFormService.onChangeCaseStageEmiter
      .pipe(
        takeUntil(this._destroy$)
      )
      .subscribe((data: any) => {
        if (data.value === CaseStatusEnum.CLOSED_RESOLVED || data.value === CaseStatusEnum.CLOSED_NOT_RESOLVED) {
          this.showVerifiedKnowledge = true;
          this.setNextActionValidators(0);
          this.caseForm.get('nextActionStatus').setValue(NextActionStatusEmum.Closed);
        } else {
          this.showVerifiedKnowledge = false;
          this.caseForm.get('verifiedKnowledge').setValue(false);
        }
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: BeforeUnloadEvent): void {
    this.discardChangesService.setUnsavedChangesState(this.caseForm.dirty || this.linksForm.dirty);

    const hasChanges = this.discardChangesService.getUnsavedChangesState();
    if (hasChanges) {
      event.preventDefault();
      event.returnValue = this.translate.instant('messages.unsaved_changes_message');
    }
  }

  unlockCase() {
    if (this.lockUser && this.lockUser.isEditable) {
      this.caseLockService
        .unlockCase(this.caseId as number)
        .pipe(first())
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.unlockCase();
    this.sidebarService.ticketEscalationData = null;
    this.sidebarService.ticketEscalationId = 0;
    this.caseFormService.selectedSerialForm = undefined;
    this._destroy$.next(undefined);
    this._destroy$.complete();
    this._destroySecond$.next(undefined);
    this._destroySecond$.complete();
  }
}
